import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { TextField } from '../../../components/ui/Field';
import { ErrorState } from '../../../components/ui/Page';
import { readPreference } from '../../../utils/storage';
import { useAuth } from '../AuthProvider';
import { AuthLayout } from './AuthLayout';

const schema = z.object({
  workspace: z.string().trim().toLowerCase().min(1, 'Enter your workspace'),
  email: z.string().trim().email('Enter a valid email'),
  password: z.string().min(1, 'Enter your password'),
});
type Values = z.infer<typeof schema>;

export function homeFor(type: 'tenant' | 'platform' | undefined): string {
  return type === 'platform' ? '/platform' : '/app';
}

export function LoginPage() {
  const { status, me, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<unknown>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { workspace: readPreference('workspace') ?? '', email: '', password: '' },
  });

  if (status === 'authenticated') return <Navigate to={homeFor(me?.user.type)} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await login(values);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from.startsWith('/app') ? from : '/app', { replace: true });
    } catch (err) {
      setError(err);
      form.resetField('password');
    }
  });

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use your business workspace and work email."
      footer={
        <Link to="/platform/login" className="hover:text-fg">
          Platform administrator sign-in
        </Link>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error ? <ErrorState error={error} /> : null}
        <TextField
          label="Workspace"
          hint="The short name of your business account, e.g. acme"
          autoComplete="organization"
          autoCapitalize="none"
          error={form.formState.errors.workspace?.message}
          {...form.register('workspace')}
        />
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <div className="flex items-center justify-between">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-brand-700 hover:underline dark:text-brand-300"
          >
            Forgot password?
          </Link>
          <Button type="submit" loading={form.formState.isSubmitting}>
            Sign in
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}

const platformSchema = schema.omit({ workspace: true });

export function PlatformLoginPage() {
  const { status, me, platformLogin } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const form = useForm<z.infer<typeof platformSchema>>({
    resolver: zodResolver(platformSchema),
    defaultValues: { email: '', password: '' },
  });

  if (status === 'authenticated') return <Navigate to={homeFor(me?.user.type)} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await platformLogin(values);
      navigate('/platform', { replace: true });
    } catch (err) {
      setError(err);
      form.resetField('password');
    }
  });

  return (
    <AuthLayout
      title="Platform console"
      subtitle="Sign in as a platform administrator."
      footer={<Link to="/login">Business sign-in</Link>}
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error ? <ErrorState error={error} /> : null}
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
